import React, { useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { Button, Modal, Form } from "react-bootstrap";
import Loader from "views/universal/Loader/Loader";
import moment from "moment";
import { savOperations } from "state/ducks/store/sav";
import { orderOperations } from "state/ducks/store/order";
import Steps from "./Steps";
import SendHistoryList from "../Common/SendHistoryList";
import { formatOrderedBefore } from "../Common/OrderedBeforeFilter";

const Message = (props) => {

  const savPage = useSelector(
    (state) => state.store.sav.admin,
    shallowEqual
  );

  const [filterSelected, setFilterSelected] = useState({});
  const [rangeDates, setRangeDates] = useState({ start: moment(), end: moment() });
  const [orderedBefore, setOrderedBefore] = useState({ date: null, time: '' });
  const [response, setResponse]             = useState(false); // {'total', 'success', 'fails'}
  const [orders, setOrders]                 = useState([]);
  const [isTruncated, setIsTruncated]       = useState(false);
  const [totalCount, setTotalCount]         = useState(0);
  const [showHistory, setShowHistory]       = useState(false);
  const [historyDate, setHistoryDate]       = useState(moment().format("YYYY-MM-DD"));

  const dispatch = useDispatch();

  const handleDateChange = (attr, value) => {
    if (attr === 'orderedBefore') {
      setOrderedBefore(value);
      return;
    }
    setRangeDates(value);
  };

  /**
   * Récupère les commandes (mêmes paramètres que CreditPool)
   * Retourne un tableau
   */
  const getOrders = async () => {
    let params = {
      deliveryType  : filterSelected.deliveryType && filterSelected.deliveryType.value,
      startDate     : rangeDates && rangeDates.start && moment(rangeDates.start).format("YYYY-MM-DD"),
      endDate       : rangeDates && rangeDates.end && moment(rangeDates.end).format("YYYY-MM-DD"),
      orderedBefore : formatOrderedBefore(orderedBefore),
      customers     : filterSelected.customers && filterSelected.customers.map((value) => value.value),
      vehicles      : filterSelected.vehicles && filterSelected.vehicles.map((value) => value.value),
      spots         : filterSelected.spots && filterSelected.spots.map((value) => value.value),
      excludedSpots : filterSelected.excludedSpots && filterSelected.excludedSpots.map((value) => value.value),
      establishment : filterSelected.establishment && filterSelected.establishment.value,
      products      : filterSelected.products && filterSelected.products.filter((value) => {
        let type = value.value.split('#');
        return (type.length === 2 && type[1] === 'product');
      }).map((value) => parseInt(value.value.split('#')[0], 10)),
      ingredients    : filterSelected.products && filterSelected.products.filter((value) => {
        let type = value.value.split('#');
        return (type.length === 2 && type[1] === 'ingredient');
      }).map((value) => parseInt(value.value.split('#')[0], 10)),
      groups: ['order_info', 'order_multiple']
    };

    return dispatch(orderOperations.getOrders(params))
    .then(
      (result) => {
        if (result?.data?.result?.orders) {
          setOrders(result.data.result.orders);
          setIsTruncated(result.data.result.isTruncated || false);
          setTotalCount(result.data.result.totalCount || 0);
          return result.data.result.orders;
        }
        setOrders([]);
        setIsTruncated(false);
        setTotalCount(0);
        return [];
      },
      (error) => {
        throw new Error('No data')
      }
    )
    .finally(() => {
    })
    .catch((e) => {
      return [];
    });
  }

  /**
   * Envoie le sms
   */
  const sendSMS = async (cause, start, end, customContent = null, isDelay = false) => {
    let orderIds = orders.map((order, key) => order.id);
    return dispatch(savOperations.sendSMS(orderIds, cause, start, end, customContent, false, isDelay))
    .then(
      (result) => {
        if (result?.data?.result) {
          setResponse(result.data.result);
          return result.data.result;
        }
        throw new Error('No data')
      },
      (error) => {
        throw new Error('No data')
      }
    )
    .finally(() => {
    })
    .catch((e) => {
      return [];
    });
  }

  /**
   * Envoie l'email
   */
  const sendEmail = async (emailContent, isDelay = false) => {
    let orderIds = orders.map((order) => order.id);
    return dispatch(savOperations.sendEmail(orderIds, emailContent, isDelay))
    .then(
      (result) => {
        if (result?.data?.result) {
          setResponse(result.data.result);
          return result.data.result;
        }
        throw new Error('No data')
      },
      (error) => {
        throw new Error('No data')
      }
    )
    .catch((e) => {
      return [];
    });
  }

  /**
   * Gère le changement des variables
   */
  const handleFiltersChange = (filters) => {
    setFilterSelected(filters);
  }

  return (
    <>
      {!savPage.isInit ? (
        <Loader items={2} />
      ) : (
        <>
          <div className="text-right mt-2">
            <Button
              variant="outline-dark"
              size="sm"
              onClick={() => setShowHistory(true)}
            >
              <span role="img" aria-label="historique">📨</span> Historique des envois
            </Button>
          </div>

          <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Historique des envois SMS / Email</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group>
                <Form.Label>Jour</Form.Label>
                <Form.Control
                  type="date"
                  value={historyDate}
                  onChange={(e) => e.target.value && setHistoryDate(e.target.value)}
                />
              </Form.Group>
              <SendHistoryList date={historyDate} />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setShowHistory(false)}>
                Fermer
              </Button>
            </Modal.Footer>
          </Modal>

          <Steps
            handleFiltersChange={handleFiltersChange}
            handleDateChange={handleDateChange}
            getOrders={getOrders}
            orders={orders}
            isTruncated={isTruncated}
            totalCount={totalCount}
            sendSMS={sendSMS}
            sendEmail={sendEmail}
            smsModel={savPage.smsModel}
            vehicles={savPage.vehicles}
            spots={savPage.spots}
            establishments={savPage.establishments}
            products={savPage.products}
            customers={savPage.customers}
            rangeDates={rangeDates}
            orderedBefore={orderedBefore}
            savIsLoading={savPage.isLoading}
            response={response}
            filterSelected={filterSelected}
          />
        </>
      )}
    </>
  );
};

Message.propTypes = {};

Message.defaultProps = {};

export default Message;
